/**
 * The asset register (BR-09).
 *
 * A shed, a chaff cutter, a water pump: things the farm owns that are worth
 * less every month. Book value is what makes that visible — the ৳1,20,000 shed
 * is not still worth ৳1,20,000 three years in, and a farm that believes it is
 * will be surprised at the first honest valuation.
 *
 * Accumulated depreciation is summed by the server from posted entries, not
 * projected from the schedule, so the figure here matches the ledger exactly.
 *
 * The register was read-only until now, which made BR-09 unreachable: the rule
 * says anything over ৳5,000 lasting more than a year is an asset rather than an
 * expense, and with no way to record one the only option was to file the shed
 * as a month's spending — the exact distortion BR-09 exists to prevent.
 */

import { ASSET_CAPITALIZATION_THRESHOLD, toMoney } from '@agrofarm/shared';
import { useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { formatDate, todayInDhaka } from '../../lib/date.js';
import { useAuth } from '../auth/AuthContext.js';
import { NewCategoryForm } from './NewCategoryForm.js';
import { useAssetCategories, useAssets, useCreateAsset, type AssetCategory } from './api.js';

/**
 * A money string the API will accept: digits, optionally two decimals.
 *
 * Checked as text rather than parsed. `Number('1200.50')` would hand the rest
 * of this form a float, and MONEY-1 exists because that is how a paisa goes
 * missing between the box and the ledger.
 */
const MONEY_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

/**
 * Recording a new asset.
 *
 * No keypad here, unlike the expense form. That keypad answers §9.5 — a partner
 * in a feed shop, one hand on the phone, sixty seconds. Buying a shed is a desk
 * job done once a year with the invoice in front of you, and a plain field lets
 * you check the figure against the paper rather than a running total.
 */
function AssetForm({
  onSaved,
  canManageCategories,
}: {
  onSaved: () => void;
  canManageCategories: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const createAsset = useCreateAsset();
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  const { data: categories = [] } = useAssetCategories();
  // Only a live category can take a new asset; the server refuses a retired
  // one regardless (AP-1), and offering it would be an invitation to be told no.
  const usable = categories.filter((option) => option.isActive);

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [purchaseDate, setPurchaseDate] = useState(todayInDhaka());
  const [cost, setCost] = useState('');
  const [salvageValue, setSalvageValue] = useState('');
  /** Empty means "use the category default", which is the common case. */
  const [usefulLifeYears, setUsefulLifeYears] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const costIsMoney = MONEY_DRAFT.test(cost);

  /**
   * BR-09 — at or below ৳5,000 it is an ordinary expense, not an asset.
   *
   * Compared as Decimals, never as numbers: this is a money comparison and it
   * decides which of two books the ৳5,000 belongs in. The server enforces the
   * same rule and is authoritative (AP-1); saying it here means a partner
   * learns it before typing the rest.
   */
  const belowThreshold = costIsMoney && !toMoney(cost).greaterThan(toMoney(ASSET_CAPITALIZATION_THRESHOLD));

  const chosen: AssetCategory | undefined = usable.find((option) => option.id === categoryId) ?? usable[0];

  const canSubmit =
    name.trim().length >= 2 && costIsMoney && !belowThreshold && chosen !== undefined && !createAsset.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});

    try {
      await createAsset.mutateAsync({
        name: name.trim(),
        categoryId: chosen!.id,
        purchaseDate,
        cost,
        ...(salvageValue.trim() === '' ? {} : { salvageValue: salvageValue.trim() }),
        // Omitted rather than sent as the default, so the server stays the one
        // place that decides what a shed's useful life is.
        ...(usefulLifeYears.trim() === '' ? {} : { usefulLifeYears: Number(usefulLifeYears) }),
      });

      // Only once the server has it (NFR-2).
      setName('');
      setCost('');
      setSalvageValue('');
      setUsefulLifeYears('');
      onSaved();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form className="mb-5 rounded-xl bg-white p-4 shadow-sm" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <h2 className="mb-3 text-base font-semibold text-gray-800">{t('finance.assets.addTitle')}</h2>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="asset-name">
        {t('finance.assets.name')}
      </label>
      <input
        id="asset-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={120}
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {fieldErrors['name'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['name']}</p>}

      <fieldset className="mb-4 mt-3">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('finance.assets.categoryLabel')}</legend>
        <div className="flex flex-wrap gap-2">
          {usable.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setCategoryId(option.id)}
              aria-pressed={chosen?.id === option.id}
              className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                chosen?.id === option.id ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {option.name}
            </button>
          ))}
        </div>
        {/* The life that will be used unless it is overridden below. Shown
            because it is what decides the monthly figure, and a partner who
            disagrees with it should find out now rather than in a year. */}
        {chosen !== undefined && (
          <p className="mt-2 text-xs text-gray-500">
            {t('finance.assets.defaultLife', { years: chosen.usefulLifeYears })}
          </p>
        )}

        {canManageCategories &&
          (isAddingCategory ? (
            <NewCategoryForm
              onAdded={(created) => {
                // Select what was just added: the partner created it because it
                // is the one they need.
                setCategoryId(created.id);
                setIsAddingCategory(false);
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setIsAddingCategory(true)}
              className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
            >
              {t('finance.assets.newCategory')}
            </button>
          ))}
      </fieldset>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="asset-cost">
        {t('finance.assets.cost')}
      </label>
      <input
        id="asset-cost"
        inputMode="decimal"
        value={cost}
        onChange={(event) => setCost(event.target.value)}
        placeholder="0.00"
        className="min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 tabular-nums"
      />
      {belowThreshold && (
        <p className="mt-1 text-sm text-amber-700">
          {t('finance.assets.belowThreshold', { amount: formatMoney(ASSET_CAPITALIZATION_THRESHOLD) })}
        </p>
      )}
      {fieldErrors['cost'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['cost']}</p>}

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="asset-date">
        {t('finance.assets.purchaseDate')}
      </label>
      <input
        id="asset-date"
        type="date"
        value={purchaseDate}
        max={todayInDhaka()}
        onChange={(event) => setPurchaseDate(event.target.value)}
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {fieldErrors['purchaseDate'] !== undefined && (
        <p className="mt-1 text-sm text-red-700">{fieldErrors['purchaseDate']}</p>
      )}

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="asset-salvage">
        {t('finance.assets.salvage')}
      </label>
      <input
        id="asset-salvage"
        inputMode="decimal"
        value={salvageValue}
        onChange={(event) => setSalvageValue(event.target.value)}
        placeholder="0.00"
        className="min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 tabular-nums"
      />
      <p className="mt-1 text-xs text-gray-500">{t('finance.assets.salvageHint')}</p>
      {fieldErrors['salvageValue'] !== undefined && (
        <p className="mt-1 text-sm text-red-700">{fieldErrors['salvageValue']}</p>
      )}

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="asset-life">
        {t('finance.assets.life')}
      </label>
      <input
        id="asset-life"
        inputMode="numeric"
        value={usefulLifeYears}
        onChange={(event) => setUsefulLifeYears(event.target.value)}
        placeholder={String(chosen?.usefulLifeYears ?? '')}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 tabular-nums"
      />
      {fieldErrors['usefulLifeYears'] !== undefined && (
        <p className="mt-1 text-sm text-red-700">{fieldErrors['usefulLifeYears']}</p>
      )}

      {/* An asset row is not a payment. Saying so here is the difference
          between a shed that is recorded once and one that is recorded twice. */}
      <p className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-600">{t('finance.assets.notAnExpense')}</p>

      {errorKey !== null && (
        <div role="alert" className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-4 min-h-[52px] w-full rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
      >
        {t(createAsset.isPending ? 'finance.assets.saving' : 'finance.assets.save')}
      </button>
    </form>
  );
}

export function AssetsScreen(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: assets = [], isLoading, isError, refetch } = useAssets();
  const [isAdding, setIsAdding] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // POST /assets is open to admins and partners, not staff — a caretaker
  // records the day's work, not what the farm owns.
  const canAdd = user?.role === 'admin' || user?.role === 'partner';

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('finance.assets.title')}</h1>

      {canAdd &&
        (isAdding ? (
          <AssetForm
            canManageCategories={user?.role === 'admin'}
            onSaved={() => {
              setIsAdding(false);
              setJustSaved(true);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setIsAdding(true);
              setJustSaved(false);
            }}
            className="mb-4 flex min-h-touch w-full items-center justify-between rounded-xl bg-green-700 px-4 py-3 text-white"
          >
            <span className="text-sm font-medium">{t('finance.assets.add')}</span>
            <span aria-hidden="true">+</span>
          </button>
        ))}

      {justSaved && (
        <p role="status" className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('finance.assets.saved')}
        </p>
      )}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {assets.length === 0 && !isLoading && (
        <p className="py-8 text-center text-gray-500">{t('finance.assets.empty')}</p>
      )}

      <ul className="space-y-3">
        {assets.map((asset) => (
          <li key={asset.id} className="rounded-xl bg-white p-4 shadow-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate font-medium text-gray-800">{asset.name}</span>
              <span className="shrink-0 text-lg font-semibold tabular-nums text-gray-900">
                {formatMoney(asset.bookValue)}
              </span>
            </div>

            <p className="text-xs text-gray-500">
              {asset.categoryName} · {formatDate(asset.purchaseDate)}
              {asset.status !== 'active' && ` · ${t(`finance.assets.status.${asset.status}`)}`}
            </p>

            <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div>
                <dt className="text-xs text-gray-500">{t('finance.assets.cost')}</dt>
                <dd className="tabular-nums text-gray-800">{formatMoney(asset.cost)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">{t('finance.assets.written')}</dt>
                <dd className="tabular-nums text-gray-800">{formatMoney(asset.accumulatedDepreciation)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">{t('finance.assets.monthly')}</dt>
                <dd className="tabular-nums text-gray-800">{formatMoney(asset.monthlyDepreciation)}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
